import { ReactNode } from 'react';
import { t } from '../../lib/i18n';
import { Graph, STEP_LABEL, stepSummary, StepType, TRIGGER_LABEL, triggerText } from '../model';

/**
 * The body of a journey node, shared by the read-only canvas (JourneyCanvas, also in «Маяк») and the editor
 * (docs/pilot/SPEC.md §12.5). Only the model is imported here, so «Маяк» can use it through the `@web/journeys` alias.
 */

const TYPE_ICON: Record<StepType, ReactNode> = {
  wait: <path d="M12 7v5l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />,
  wait_for_event: <path d="M3 12h4l2.5-6 4 12 2.5-6H21" />,
  branch: <path d="M6 3v6a6 6 0 0 0 6 6h6M6 9v12M18 15l-3-3m3 3-3 3" />,
  split: <path d="M12 3v6M12 9l-6 6v6M12 9l6 6v6" />,
  send_email: <><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="m4 7 8 6 8-6" /></>,
  // «Уведомление»: a wave carrying a signal (one icon family with the channels section)
  send_notification: <><path d="M3 15c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2" /><path d="M3 19c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2" /><path d="M12 3v6M9 6l3-3 3 3" /></>,
  webhook: <path d="M10 14a3 3 0 1 1-4.2-4.2L9 6.6M14 10a3 3 0 1 1 4.2 4.2L15 17.4M8 16h8" />,
  update_contact: <><circle cx="12" cy="8" r="3.6" /><path d="M4.5 20c1.3-3.6 4.2-5.4 7.5-5.4M17 15v6M14 18h6" /></>,
  digest: <path d="M5 4h14v4H5zM5 10h14v4H5zM5 16h14v4H5z" />,
  exit: <path d="M14 4h5v16h-5M10 8l-4 4 4 4M6 12h10" />,
};

export function StepIcon({ type, size = 16 }: { type: StepType; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {TYPE_ICON[type]}
    </svg>
  );
}

/** Head (icon, kind, id) and the one-line summary of a step. */
export function NodeBody({ graph, id, unreachable, badges }: { graph: Graph; id: string; unreachable?: boolean; badges?: ReactNode }) {
  const step = graph.steps[id];
  if (!step) return null;
  return (
    <>
      <span className="jc-head">
        <span className="jc-icon"><StepIcon type={step.type} /></span>
        <span className="jc-kind">{t(STEP_LABEL[step.type])}</span>
        <code>{id}</code>
      </span>
      <span className="jc-sum">{stepSummary(step)}</span>
      {(unreachable || badges) && (
        <span className="jc-flags">
          {unreachable && <span className="jc-flag">{t('недостижим')}</span>}
          {badges}
        </span>
      )}
    </>
  );
}

/** The trigger node's body. */
export function TriggerBody({ graph }: { graph: Graph }) {
  return (
    <>
      <span className="jc-head"><span className="jc-kind">{t('Старт')} · {t(TRIGGER_LABEL[graph.trigger.type])}</span></span>
      <span className="jc-sum">{triggerText(graph.trigger)}</span>
    </>
  );
}
